/* eslint-disable react-refresh/only-export-components -- provider + its hooks live together on purpose */
/**
 * React side of the user session (#1809, see userSession.ts). <UserSessionProvider> sits above the routes, so changing
 * page neither subscribes nor reads anything. Pages read with useBalances, usePerpPositions, useOpenOrders, useFills and
 * useOrderHistory.
 *
 * The session changes in the render that changes the account, so no page ever sees one account's numbers under another,
 * and a reader asking for anyone but the session's user gets nothing. At most one session exists; while ghosting, the
 * connected wallet has only its toast streams (OrderToasts).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useInfoClient, useWs } from '../hl/react';
import { useViewedAccount, useWallet } from '../wallet/context';
import type { FillsHeld } from './sessionFills';
import type { HistoryHeld } from './sessionHistory';
import { UserSession, type HeldState, type SessionTiming } from './userSession';

const SessionContext = createContext<UserSession | null>(null);

export interface HoldUserSessionProps {
  /** Whose data to hold; null holds nothing. */
  user: string | null;
  /** `user` is the connected wallet. */
  own: boolean;
  /** Test seam, read once. */
  timing?: SessionTiming;
  children: ReactNode;
}

export function HoldUserSession({ user, own, timing, children }: HoldUserSessionProps) {
  const ws = useWs('user');
  const info = useInfoClient();
  const timingRef = useRef(timing);
  const lower = user ? user.toLowerCase() : null;
  const session = useMemo(
    () => (lower ? new UserSession(ws, info, lower, timingRef.current) : null),
    [ws, info, lower],
  );
  useEffect(() => {
    if (!session) return;
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => (session && own ? session.holdOwnEvents() : undefined), [session, own]);
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

/** Holds the session of the account on screen: the ghost address, otherwise the connected wallet. */
export function UserSessionProvider({ timing, children }: { timing?: SessionTiming; children: ReactNode }) {
  const wallet = useWallet();
  const viewed = useViewedAccount();
  const user = viewed.address ? viewed.address.toLowerCase() : null;
  const own = wallet.status === 'connected' && wallet.address?.toLowerCase() === user;
  return (
    <HoldUserSession user={user} own={own} timing={timing}>
      {children}
    </HoldUserSession>
  );
}

export function useUserSession(): UserSession | null {
  return useContext(SessionContext);
}

const IDLE: HeldState<never> = { data: undefined, isLoading: false, error: null, source: null, stale: false };
const WAITING: HeldState<never> = { data: undefined, isLoading: true, error: null, source: null, stale: true };
const noSubscribe = () => () => undefined;

type HeldKind = 'balances' | 'positions' | 'orders';
type DataOf<K extends HeldKind> = NonNullable<ReturnType<UserSession[K]['getState']>['data']>;

/** What the session holds for `address`. Idle with no address, waiting for an address that is not the session's. */
export function useHeld<K extends HeldKind>(kind: K, address: string | null | undefined): HeldState<DataOf<K>> {
  const session = useUserSession();
  const user = address ? address.toLowerCase() : null;
  const stream = session && session.user === user ? session[kind] : null;
  const fallback = user ? WAITING : IDLE;
  const state = useSyncExternalStore<HeldState<unknown>>(
    stream ? stream.subscribe : noSubscribe,
    stream ? stream.getState : () => fallback,
  );
  return state as HeldState<DataOf<K>>;
}

const NO_FILLS: FillsHeld = { fills: [], loading: false, complete: true, capped: false, error: null };
const WAITING_FILLS: FillsHeld = { fills: [], loading: true, complete: false, capped: false, error: null };

/** The fills the session holds for `address`, on the same terms as useHeld. */
export function useHeldFills(address: string | null | undefined): FillsHeld {
  const session = useUserSession();
  const user = address ? address.toLowerCase() : null;
  const fills = session && session.user === user ? session.fills : null;
  const fallback = user ? WAITING_FILLS : NO_FILLS;
  return useSyncExternalStore(fills ? fills.subscribe : noSubscribe, fills ? fills.getState : () => fallback);
}

const NO_HISTORY: HistoryHeld = { history: [], loading: false, complete: true, error: null };
const WAITING_HISTORY: HistoryHeld = { history: [], loading: true, complete: false, error: null };

/** The order history the session holds for `address`, on the same terms as useHeld. */
export function useHeldHistory(address: string | null | undefined): HistoryHeld {
  const session = useUserSession();
  const user = address ? address.toLowerCase() : null;
  const history = session && session.user === user ? session.history : null;
  const fallback = user ? WAITING_HISTORY : NO_HISTORY;
  return useSyncExternalStore(history ? history.subscribe : noSubscribe, history ? history.getState : () => fallback);
}
